import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ProviderChip } from '@/components/ProviderChip';
import { KeyRound, Loader2, ScanEye } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import BillingCard from '@/components/BillingCard';

export default function SettingsPage() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [pplxConnected, setPplxConnected] = useState(false);
  const [pplxKey, setPplxKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/settings/providers');
      setProviders(r.data.providers || []);
      setPplxConnected(!!r.data.perplexity_key_connected);
    } catch (_e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const savePerplexity = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/providers/perplexity', { perplexity_api_key: pplxKey });
      toast.success(pplxKey ? 'Perplexity API key saved' : 'Perplexity API key removed');
      setPplxKey('');
      await load();
    } catch (_e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Configure providers and your account.</p>
      </div>

      {/* Account */}
      <Card className="border p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-muted-foreground">
            <ScanEye className="h-5 w-5" />
          </div>
          <div>
            <div className="font-medium">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
          </div>
        </div>
      </Card>

      {/* Billing & Plan */}
      <BillingCard />

      {/* Providers */}
      <Card className="border p-6">
        <div className="font-display text-base font-semibold">AI Providers</div>
        <p className="mt-1 text-sm text-muted-foreground">These are the answer engines GEOrush can query when you run a scan.</p>
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : providers.map((p) => (
            <div key={p.key} className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-3">
                <ProviderChip providerKey={p.key} comingSoon={!p.available} active={p.available} />
                <div>
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="text-xs text-muted-foreground">{p.available ? (p.model || 'Enabled') : (p.reason || 'Coming soon')}</div>
                </div>
              </div>
              <div className="text-xs">
                {p.available
                  ? <span className="text-emerald-600 font-medium">Enabled</span>
                  : <span className="text-muted-foreground">{p.state === 'coming-soon' || !p.available ? 'Coming soon' : ''}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Perplexity key */}
      <Card className="border p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <div className="font-display text-base font-semibold">Perplexity API key</div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Bring your own Perplexity API key to unlock Perplexity queries when the integration ships.</p>
        <div className="mt-4 space-y-2">
          <div className="text-xs text-muted-foreground">Status: {pplxConnected ? <span className="text-emerald-600 font-medium">Connected</span> : <span>Not connected</span>}</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="pplx" className="sr-only">Perplexity key</Label>
              <Input id="pplx" type="password" placeholder="pplx-••••••••••••••••" value={pplxKey} onChange={(e) => setPplxKey(e.target.value)} data-testid="settings-perplexity-api-key-input" />
            </div>
            <Button onClick={savePerplexity} disabled={saving} data-testid="settings-perplexity-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pplxKey ? 'Save key' : (pplxConnected ? 'Remove key' : 'Save')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
